import { useCallback, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authRequest } from '../../../api/auth.js';
import { useUsedFolderUUID } from './currentFolderUUID';
import { useSelector } from 'react-redux';
import { useSetCurrentOrgUUID } from '../utils/useParams.js';

const FOLDER_INFO_VERSION = 'folderInfo-v2'

const getFolderInfo = async ({
  folderUUID,
  apiOptions = { noFile: false, noUrl: false, combine: false },
}) => {
  /** Main API call to get data for a given folder from S9 backend */
  console.debug('Main API call, data for a folder', folderUUID);
  let url = `/api/datamanage/info/folder/${folderUUID}`;
  let params = [];
  if (apiOptions.noFile) {
    params.push('noFile=true');
  }
  if (apiOptions.noUrl) {
    params.push('noUrl=true');
  }
  if (apiOptions.combine) {
    params.push('combine=true');
  }
  if (params.length > 0) {
    url += '?' + params.join('&');
  }

  // A 404 here means the folder is gone (deleted, or a stale bookmark /
  // org_last_url). StorageManagerProvider redirects to the org root, so the
  // generic error toast would only be noise.
  const res = await authRequest.get(url, { suppress404Toast: true });
  console.log('getFolderInfo response', res.data);

  return res.data;
};

// NOTE: written with explicit && checks on purpose. The frontend build runs on
// node:14 and its babel config does not transpile optional chaining, so webpack
// fails to parse it. Do not "simplify" this line.
export const isNotFound = error =>
  Boolean(error && error.response && error.response.status === 404);

// Retrying a 404 cannot succeed — the folder does not exist. Without this,
// react-query's default retries fire the request (and previously the toast)
// several times over.
const retryUnlessNotFound = (failureCount, error) =>
  !isNotFound(error) && failureCount < 3;

export const useFolderInfo = ({
  folderUUID = '',
  updateCurrentOrgUUID = true,
  useErrorBoundary = undefined,
  apiOptions = { noFile: false, noUrl: true, combine: true },
} = {}) => {
  /* デフォルトは現在地を取得するオプション   */
  const usedFolderUUID = useUsedFolderUUID(folderUUID);

  console.log('Folder info:', FOLDER_INFO_VERSION, folderUUID);

  const currentOrgUUID = useSelector(
    state => state.storageManager.currentOrgUUID
  );
  const setCurrentOrgUUID = useSetCurrentOrgUUID();

  const _onSuccess = useCallback(
    data => {
      if (updateCurrentOrgUUID) {
        if (data.current_folder.org != currentOrgUUID) {
          if (data.current_folder.org === null) {
            setCurrentOrgUUID('');
          } else {
            setCurrentOrgUUID(data.current_folder.org);
          }
        }
      }
    },
    [currentOrgUUID, setCurrentOrgUUID, updateCurrentOrgUUID]
  );

  return useQuery({
    queryKey: [FOLDER_INFO_VERSION, usedFolderUUID, apiOptions],
    queryFn: () =>
      getFolderInfo({
        folderUUID: usedFolderUUID,
        apiOptions: apiOptions,
      }),
    enabled: usedFolderUUID !== '', // folder uuid が存在するまで待機
    onSuccess: _onSuccess,
    retry: retryUnlessNotFound,
    useErrorBoundary,
    networkMode: 'always', // Forces the query to run even offline
  });
};

export function useFolderInfoInvalidate({
  // デフォルトは現在地を取得するオプション
  folderUUID = '',
  apiOptions = { noFile: false, noUrl: true, combine: true },
} = {}) {
  const queryClient = useQueryClient();
  const usedFolderUUID = useUsedFolderUUID(folderUUID);
  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries([FOLDER_INFO_VERSION, usedFolderUUID, apiOptions]);
  }, [apiOptions, queryClient, usedFolderUUID]);
  return invalidateQueries;
}

export function useFolderInfoReset() {
  const queryClient = useQueryClient();
  const currentFolderUUID = useSelector(
    state => state.storageManager.currentFolderUUID
  );
  const resetQueries = useCallback(
    ({
      folderUUID = '',
      apiOptions = { noFile: false, noUrl: true, combine: true },
    } = {}) => {
      if (folderUUID == '') {
        // 現在地更新
        queryClient.resetQueries([FOLDER_INFO_VERSION, currentFolderUUID, apiOptions]);
      } else {
        // 指定場所更新
        queryClient.resetQueries([FOLDER_INFO_VERSION, folderUUID, apiOptions]);
      }
    },
    [currentFolderUUID, queryClient]
  );
  return resetQueries;
}
